import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
    LayoutDashboard, GraduationCap, Briefcase, Code2, Layers, 
    Mail, Settings, LogOut, Menu, X, Plus, Trash2, Edit, Eye,
    FileText, Upload, Loader2, ChevronLeft, Search, Archive, User, Cpu, Sun, Moon, CreditCard
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import ApplicationsSection from '../components/admin/ApplicationsSection';
import PaymentsSection from '../components/admin/PaymentsSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Login Component
const AdminLogin = ({ onLogin }) => {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { t } = useLanguage();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        const result = await onLogin(password);
        if (!result.success) {
            setError(result.error);
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login">
            <div className="glass-card p-8 w-full max-w-md">
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold text-white mb-2">Administration</h1>
                    <p className="text-white font-medium">{t('Accès réservé', 'Restricted access')}</p>
                </div>
                
                <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                        <Label htmlFor="password" className="text-white font-bold mb-2 block">
                            {t('Mot de passe', 'Password')}
                        </Label>
                        <Input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="bg-slate-700 border-slate-500 text-white font-medium"
                            placeholder="••••••••"
                            data-testid="admin-password-input"
                        />
                    </div>
                    
                    {error && (
                        <p className="text-red-400 font-medium text-sm mb-4" data-testid="admin-error">{error}</p>
                    )}
                    
                    <Button 
                        type="submit" 
                        disabled={loading}
                        className="w-full bg-blue-600 hover:bg-blue-500 font-bold"
                        data-testid="admin-login-btn"
                    >
                        {loading ? <Loader2 className="animate-spin" /> : t('Connexion', 'Login')}
                    </Button>
                </form>
                
                <div className="mt-6 text-center">
                    <Link to="/" className="text-white font-medium hover:text-blue-400 text-sm">
                        <ChevronLeft className="inline w-4 h-4" /> {t('Retour au site', 'Back to site')}
                    </Link>
                </div>
            </div>
        </div>
    );
};

// Sidebar Navigation
const AdminSidebar = ({ activeSection, setActiveSection, isOpen, setIsOpen }) => {
    const { logout } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        const fetchUnread = async () => {
            try {
                const token = localStorage.getItem('dkr-token');
                const response = await axios.get(`${API}/contacts/unread-count`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUnreadCount(response.data.count);
            } catch (error) {
                console.error('Error fetching unread count:', error);
            }
        };
        fetchUnread();
        const interval = setInterval(fetchUnread, 30000);
        return () => clearInterval(interval);
    }, []);

    const navItems = [
        { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { id: 'presentation', icon: User, label: t('Présentation', 'Presentation') },
        { id: 'technologies', icon: Cpu, label: 'Technologies' },
        { id: 'formations', icon: GraduationCap, label: 'Formations' },
        { id: 'experiences', icon: Briefcase, label: t('Expériences', 'Experiences') },
        { id: 'applications', icon: Code2, label: 'Applications' },
        { id: 'services', icon: Layers, label: 'Services' },
        { id: 'mailbox', icon: Mail, label: 'Mailbox', badge: unreadCount },
        { id: 'payments', icon: CreditCard, label: t('Paiements', 'Payments') },
    ];

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <>
            {/* Mobile overlay */}
            {isOpen && (
                <div 
                    className="lg:hidden fixed inset-0 bg-black/50 z-40"
                    onClick={() => setIsOpen(false)}
                />
            )}
            
            <aside className={`
                fixed top-0 left-0 h-full w-64 bg-slate-900/95 backdrop-blur-xl border-r border-white/10 z-50
                transform transition-transform duration-300
                ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="p-6">
                    <div className="flex items-center justify-between mb-8">
                        <Link to="/" className="text-xl font-bold text-white">
                            DKR
                        </Link>
                        <button 
                            className="lg:hidden text-slate-400"
                            onClick={() => setIsOpen(false)}
                        >
                            <X size={24} />
                        </button>
                    </div>
                    
                    <nav className="space-y-2">
                        {navItems.map(item => (
                            <button
                                key={item.id}
                                onClick={() => {
                                    setActiveSection(item.id);
                                    setIsOpen(false);
                                }}
                                className={`admin-nav-item w-full ${activeSection === item.id ? 'active' : ''}`}
                                data-testid={`nav-${item.id}`}
                            >
                                <item.icon size={20} />
                                <span className="flex-1 text-left">{item.label}</span>
                                {item.badge > 0 && (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500 text-white">
                                        {item.badge}
                                    </span>
                                )}
                            </button>
                        ))}
                    </nav>
                </div>
                
                <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10">
                    <button
                        onClick={handleLogout}
                        className="admin-nav-item w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid="admin-logout"
                    >
                        <LogOut size={20} />
                        {t('Déconnexion', 'Logout')}
                    </button>
                </div>
            </aside>
        </>
    );
};

// Dashboard Section
const DashboardSection = () => {
    const { t } = useLanguage();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const token = localStorage.getItem('dkr-token');
                const response = await axios.get(`${API}/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setStats(response.data);
            } catch (error) {
                console.error('Error fetching stats:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    if (loading) {
        return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
    }

    const statCards = [
        { label: 'Formations', value: stats?.formations || 0, icon: GraduationCap, color: 'blue' },
        { label: t('Expériences', 'Experiences'), value: stats?.experiences || 0, icon: Briefcase, color: 'violet' },
        { label: 'Applications', value: stats?.applications || 0, icon: Code2, color: 'cyan' },
        { label: 'Services', value: stats?.services || 0, icon: Layers, color: 'green' },
        { label: t('Messages non lus', 'Unread messages'), value: stats?.unread_contacts || 0, icon: Mail, color: 'orange' },
    ];

    return (
        <div data-testid="admin-dashboard">
            <h2 className="text-2xl font-bold text-white mb-8">Dashboard</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {statCards.map((stat, index) => (
                    <div key={index} className="glass-card p-6">
                        <div className="flex items-center justify-between mb-4">
                            <stat.icon className={`w-8 h-8 text-${stat.color}-400`} />
                            <span className="text-3xl font-bold text-white">{stat.value}</span>
                        </div>
                        <p className="text-slate-400">{stat.label}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Presentation Section
const PresentationSection = () => {
    const { t } = useLanguage();
    const [presentation, setPresentation] = useState({
        titre_fr: '',
        titre_en: '',
        texte_fr: '',
        texte_en: '',
        annees_experience: 15,
        linkedin_url: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const token = localStorage.getItem('dkr-token');
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    useEffect(() => {
        fetchPresentation();
    }, []);

    const fetchPresentation = async () => {
        try {
            const response = await axios.get(`${API}/presentation`);
            setPresentation(response.data);
        } catch (error) {
            console.error('Error fetching presentation:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/presentation`, presentation, authHeaders);
            toast.success(t('Présentation mise à jour', 'Presentation updated'));
        } catch (error) {
            toast.error(t('Erreur lors de la sauvegarde', 'Error saving'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
    }

    return (
        <div data-testid="admin-presentation">
            <h2 className="text-2xl font-bold text-white mb-8">{t('Présentation', 'Presentation')}</h2>
            
            <div className="glass-card p-6 space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <Label className="text-white font-bold mb-2 block">{t('Titre (FR)', 'Title (FR)')}</Label>
                        <Input
                            value={presentation.titre_fr}
                            onChange={(e) => setPresentation(prev => ({ ...prev, titre_fr: e.target.value }))}
                            className="bg-slate-700 border-slate-500 text-white font-medium"
                        />
                    </div>
                    <div>
                        <Label className="text-white font-bold mb-2 block">{t('Titre (EN)', 'Title (EN)')}</Label>
                        <Input
                            value={presentation.titre_en}
                            onChange={(e) => setPresentation(prev => ({ ...prev, titre_en: e.target.value }))}
                            className="bg-slate-700 border-slate-500 text-white font-medium"
                        />
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <Label className="text-white font-bold mb-2 block">{t('Années d\'expérience', 'Years of experience')}</Label>
                        <Input
                            type="number"
                            value={presentation.annees_experience}
                            onChange={(e) => setPresentation(prev => ({ ...prev, annees_experience: parseInt(e.target.value) || 15 }))}
                            className="bg-slate-700 border-slate-500 text-white font-medium"
                        />
                    </div>
                    <div>
                        <Label className="text-white font-bold mb-2 block">{t('URL LinkedIn', 'LinkedIn URL')}</Label>
                        <Input
                            value={presentation.linkedin_url || ''}
                            onChange={(e) => setPresentation(prev => ({ ...prev, linkedin_url: e.target.value }))}
                            className="bg-slate-700 border-slate-500 text-white font-medium"
                            placeholder="https://linkedin.com/in/votre-profil"
                        />
                    </div>
                </div>

                <div>
                    <Label className="text-white font-bold mb-2 block">{t('Texte de présentation (FR)', 'Presentation text (FR)')}</Label>
                    <Textarea
                        value={presentation.texte_fr}
                        onChange={(e) => setPresentation(prev => ({ ...prev, texte_fr: e.target.value }))}
                        className="bg-slate-700 border-slate-500 text-white font-medium"
                        rows={4}
                    />
                </div>

                <div>
                    <Label className="text-white font-bold mb-2 block">{t('Texte de présentation (EN)', 'Presentation text (EN)')}</Label>
                    <Textarea
                        value={presentation.texte_en}
                        onChange={(e) => setPresentation(prev => ({ ...prev, texte_en: e.target.value }))}
                        className="bg-slate-700 border-slate-500 text-white font-medium"
                        rows={4}
                    />
                </div>

                <Button 
                    onClick={handleSave} 
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-500 font-bold"
                >
                    {saving ? <Loader2 className="animate-spin mr-2" /> : null}
                    {t('Sauvegarder', 'Save')}
                </Button>
            </div>
        </div>
    );
};

// Technologies Section
const TechnologiesSection = () => {
    const { t } = useLanguage();
    const [technologies, setTechnologies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newTech, setNewTech] = useState({ nom: '', categorie: 'langages' });

    const token = localStorage.getItem('dkr-token');
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    const categories = [
        { value: 'langages', label: t('Langages', 'Languages') },
        { value: 'frameworks', label: 'Frameworks' },
        { value: 'databases', label: t('Bases de données', 'Databases') },
        { value: 'outils', label: t('Outils', 'Tools') },
    ];

    useEffect(() => {
        fetchTechnologies();
    }, []);

    const fetchTechnologies = async () => {
        try {
            const response = await axios.get(`${API}/technologies`);
            setTechnologies(response.data);
        } catch (error) {
            console.error('Error fetching technologies:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        if (!newTech.nom.trim()) return;
        try {
            await axios.post(`${API}/technologies`, newTech, authHeaders);
            toast.success(t('Technologie ajoutée', 'Technology added'));
            fetchTechnologies();
            setNewTech({ nom: '', categorie: 'langages' });
        } catch (error) {
            toast.error(t('Erreur', 'Error'));
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${API}/technologies/${id}`, authHeaders);
            toast.success(t('Technologie supprimée', 'Technology deleted'));
            fetchTechnologies();
        } catch (error) {
            toast.error(t('Erreur', 'Error'));
        }
    };

    const groupedTechs = technologies.reduce((acc, tech) => {
        const cat = tech.categorie || 'outils';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(tech);
        return acc;
    }, {});

    if (loading) {
        return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
    }

    return (
        <div data-testid="admin-technologies">
            <h2 className="text-2xl font-bold text-white mb-8">Technologies</h2>
            
            {/* Add new */}
            <div className="glass-card p-6 mb-8">
                <h3 className="text-lg font-bold text-white mb-4">{t('Ajouter une technologie', 'Add a technology')}</h3>
                <div className="flex flex-wrap gap-4">
                    <Input
                        value={newTech.nom}
                        onChange={(e) => setNewTech(prev => ({ ...prev, nom: e.target.value }))}
                        placeholder={t('Nom de la technologie', 'Technology name')}
                        className="bg-slate-700 border-slate-500 text-white font-medium flex-1 min-w-[200px]"
                    />
                    <Select 
                        value={newTech.categorie} 
                        onValueChange={(v) => setNewTech(prev => ({ ...prev, categorie: v }))}
                    >
                        <SelectTrigger className="bg-slate-700 border-slate-500 text-white font-medium w-48">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-500">
                            {categories.map(cat => (
                                <SelectItem key={cat.value} value={cat.value} className="text-white font-medium">
                                    {cat.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button onClick={handleAdd} className="bg-blue-600 hover:bg-blue-500 font-bold">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('Ajouter', 'Add')}
                    </Button>
                </div>
            </div>

            {/* List by category */}
            <div className="grid md:grid-cols-2 gap-6">
                {categories.map(cat => (
                    <div key={cat.value} className="glass-card p-6">
                        <h3 className="text-lg font-bold text-white mb-4">{cat.label}</h3>
                        <div className="flex flex-wrap gap-2">
                            {(groupedTechs[cat.value] || []).map(tech => (
                                <div 
                                    key={tech.id}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30"
                                >
                                    <span className="font-medium">{tech.nom}</span>
                                    <button 
                                        onClick={() => handleDelete(tech.id)}
                                        className="hover:text-red-400 transition-colors"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                            {(!groupedTechs[cat.value] || groupedTechs[cat.value].length === 0) && (
                                <p className="text-slate-500 text-sm">{t('Aucune technologie', 'No technologies')}</p>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Generic CRUD Section
const CrudSection = ({ title, endpoint, fields, renderItem }) => {
    const { t } = useLanguage();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({});

    const token = localStorage.getItem('dkr-token');
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    useEffect(() => {
        fetchItems();
    }, [endpoint]);

    const fetchItems = async () => {
        try {
            const response = await axios.get(`${API}/${endpoint}`);
            setItems(response.data);
        } catch (error) {
            console.error(`Error fetching ${endpoint}:`, error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        try {
            await axios.post(`${API}/${endpoint}`, formData, authHeaders);
            toast.success(t('Élément créé avec succès', 'Item created successfully'));
            fetchItems();
            setIsDialogOpen(false);
            setFormData({});
        } catch (error) {
            toast.error(t('Erreur lors de la création', 'Error creating item'));
        }
    };

    const handleUpdate = async () => {
        try {
            await axios.put(`${API}/${endpoint}/${editingItem.id}`, formData, authHeaders);
            toast.success(t('Élément mis à jour', 'Item updated'));
            fetchItems();
            setIsDialogOpen(false);
            setEditingItem(null);
            setFormData({});
        } catch (error) {
            toast.error(t('Erreur lors de la mise à jour', 'Error updating item'));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('Confirmer la suppression ?', 'Confirm deletion?'))) return;
        try {
            await axios.delete(`${API}/${endpoint}/${id}`, authHeaders);
            toast.success(t('Élément supprimé', 'Item deleted'));
            fetchItems();
        } catch (error) {
            toast.error(t('Erreur lors de la suppression', 'Error deleting item'));
        }
    };

    const toggleVisibility = async (item) => {
        try {
            const res = await axios.put(`${API}/${endpoint}/${item.id}/toggle-visibility`, {}, authHeaders);
            toast.success(res.data.visible ? t('Élément visible', 'Item visible') : t('Élément masqué', 'Item hidden'));
            fetchItems();
        } catch (e) { toast.error(t('Erreur', 'Error')); }
    };

    const openEdit = (item) => {
        setEditingItem(item);
        setFormData(item);
        setIsDialogOpen(true);
    };

    const openCreate = () => {
        setEditingItem(null);
        setFormData({});
        setIsDialogOpen(true);
    };

    if (loading) {
        return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
    }

    return (
        <div data-testid={`admin-${endpoint}`}>
            <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-white">{title}</h2>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-500" data-testid="add-item-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            {t('Ajouter', 'Add')}
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-slate-900 border-white/20 max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-white text-xl font-bold">
                                {editingItem ? t('Modifier', 'Edit') : t('Ajouter', 'Add')} {title}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                            {fields.map(field => (
                                <div key={field.name}>
                                    <Label className="text-white font-bold mb-2 block text-sm">{field.label}</Label>
                                    {field.type === 'textarea' ? (
                                        <Textarea
                                            value={formData[field.name] || ''}
                                            onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
                                            className="bg-slate-700 border-slate-500 text-white font-medium placeholder:text-slate-400 focus:border-blue-500"
                                            rows={4}
                                        />
                                    ) : field.type === 'select' ? (
                                        <Select 
                                            value={formData[field.name]?.toString() || ''} 
                                            onValueChange={(v) => setFormData(prev => ({ ...prev, [field.name]: field.name === 'demontrable' ? v === 'true' : v }))}
                                        >
                                            <SelectTrigger className="bg-slate-700 border-slate-500 text-white font-medium">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-700 border-slate-500">
                                                {field.options.map(opt => (
                                                    <SelectItem key={opt.value} value={opt.value} className="text-white font-medium hover:bg-slate-600">
                                                        {opt.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : field.type === 'array' ? (
                                        <Input
                                            value={Array.isArray(formData[field.name]) ? formData[field.name].join(', ') : formData[field.name] || ''}
                                            onChange={(e) => setFormData(prev => ({ 
                                                ...prev, 
                                                [field.name]: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                                            }))}
                                            className="bg-slate-700 border-slate-500 text-white font-medium placeholder:text-slate-400 focus:border-blue-500"
                                            placeholder={t('Séparés par des virgules', 'Comma separated')}
                                        />
                                    ) : (
                                        <Input
                                            type={field.type || 'text'}
                                            value={formData[field.name] || ''}
                                            onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
                                            className="bg-slate-700 border-slate-500 text-white font-medium placeholder:text-slate-400 focus:border-blue-500"
                                        />
                                    )}
                                </div>
                            ))}
                            <div className="flex justify-end gap-3 pt-4">
                                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                                    {t('Annuler', 'Cancel')}
                                </Button>
                                <Button 
                                    onClick={editingItem ? handleUpdate : handleCreate}
                                    className="bg-blue-600 hover:bg-blue-500"
                                    data-testid="save-item-btn"
                                >
                                    {editingItem ? t('Mettre à jour', 'Update') : t('Créer', 'Create')}
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="space-y-4">
                {items.length === 0 ? (
                    <div className="glass-card p-12 text-center">
                        <p className="text-slate-400">{t('Aucun élément', 'No items')}</p>
                    </div>
                ) : (
                    items.map(item => (
                        <div key={item.id} className={`glass-card p-4 flex items-center justify-between ${item.visible === false ? 'opacity-50' : ''}`} data-testid={`item-${item.id}`}>
                            <div className="flex-1 flex items-center gap-2">
                                {renderItem(item)}
                                {item.visible === false && (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400">{t('Masqué', 'Hidden')}</span>
                                )}
                            </div>
                            <div className="flex gap-1">
                                <Button size="sm" variant="ghost"
                                    onClick={() => toggleVisibility(item)}
                                    className={item.visible === false ? 'text-red-400 hover:text-green-300' : 'text-green-400 hover:text-red-300'}
                                    title={item.visible === false ? t('Afficher', 'Show') : t('Masquer', 'Hide')}
                                    data-testid={`toggle-vis-${item.id}`}>
                                    {item.visible === false ? <Eye className="w-4 h-4 opacity-50" /> : <Eye className="w-4 h-4" />}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => openEdit(item)} data-testid={`edit-${item.id}`}>
                                    <Edit className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDelete(item.id)} data-testid={`delete-${item.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

// Mailbox Section
const MailboxSection = () => {
    const { t } = useLanguage();
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedContact, setSelectedContact] = useState(null);
    const [filter, setFilter] = useState('unread');

    const token = localStorage.getItem('dkr-token');
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    useEffect(() => {
        fetchContacts();
    }, []);

    const fetchContacts = async () => {
        try {
            const response = await axios.get(`${API}/contacts`, authHeaders);
            setContacts(response.data);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (id) => {
        try {
            await axios.put(`${API}/contacts/${id}/read`, {}, authHeaders);
            fetchContacts();
        } catch (error) {
            console.error('Error marking as read:', error);
        }
    };

    const archiveContact = async (id) => {
        try {
            await axios.put(`${API}/contacts/${id}/archive`, {}, authHeaders);
            toast.success(t('Message archivé', 'Message archived'));
            fetchContacts();
            setSelectedContact(null);
        } catch (error) {
            toast.error(t('Erreur', 'Error'));
        }
    };

    const deleteContact = async (id) => {
        if (!window.confirm(t('Supprimer ce message ?', 'Delete this message?'))) return;
        try {
            await axios.delete(`${API}/contacts/${id}`, authHeaders);
            toast.success(t('Message supprimé', 'Message deleted'));
            fetchContacts();
            setSelectedContact(null);
        } catch (error) {
            toast.error(t('Erreur', 'Error'));
        }
    };

    const filteredContacts = contacts.filter(c => {
        if (filter === 'unread') return !c.lu && !c.archived;
        if (filter === 'read') return c.lu && !c.archived;
        if (filter === 'archived') return c.archived;
        return !c.archived;
    });

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    if (loading) {
        return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
    }

    return (
        <div data-testid="admin-mailbox">
            <h2 className="text-2xl font-bold text-white mb-8">Mailbox</h2>
            
            {/* Filters */}
            <div className="flex gap-2 mb-6">
                {['all', 'unread', 'read', 'archived'].map(f => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-4 py-2 rounded-full text-sm transition-colors ${
                            filter === f ? 'bg-blue-500 text-white' : 'bg-white/5 text-slate-400 hover:bg-white/10'
                        }`}
                    >
                        {f === 'all' ? t('Tous', 'All') : 
                         f === 'unread' ? t('Non lus', 'Unread') : 
                         f === 'read' ? t('Lus', 'Read') : 
                         t('Archivés', 'Archived')}
                    </button>
                ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* List */}
                <div className="lg:col-span-1 space-y-2 max-h-[600px] overflow-y-auto">
                    {filteredContacts.length === 0 ? (
                        <div className="glass-card p-8 text-center">
                            <p className="text-slate-400">{t('Aucun message', 'No messages')}</p>
                        </div>
                    ) : (
                        filteredContacts.map(contact => (
                            <div
                                key={contact.id}
                                onClick={() => {
                                    setSelectedContact(contact);
                                    if (!contact.lu) markAsRead(contact.id);
                                }}
                                className={`glass-card p-4 cursor-pointer transition-colors ${
                                    selectedContact?.id === contact.id ? 'border-blue-500/50' : ''
                                } ${!contact.lu ? 'border-l-4 border-l-blue-500' : ''}`}
                                data-testid={`contact-${contact.id}`}
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <span className="font-medium text-white truncate">{contact.nom}</span>
                                    {!contact.lu && <span className="w-2 h-2 rounded-full bg-blue-500"></span>}
                                </div>
                                <p className="text-sm text-slate-400 truncate">{contact.sujet}</p>
                                <p className="text-xs text-slate-500 mt-1">{formatDate(contact.created_at)}</p>
                            </div>
                        ))
                    )}
                </div>

                {/* Detail */}
                <div className="lg:col-span-2">
                    {selectedContact ? (
                        <div className="glass-card p-6" data-testid="contact-detail">
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <h3 className="text-xl font-semibold text-white">{selectedContact.nom}</h3>
                                    {selectedContact.raison_sociale && (
                                        <p className="text-slate-400">{selectedContact.raison_sociale}</p>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <Button size="sm" variant="ghost" onClick={() => archiveContact(selectedContact.id)}>
                                        <Archive className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deleteContact(selectedContact.id)}>
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                            
                            <div className="space-y-4 mb-6">
                                <div className="flex items-center gap-4 text-sm">
                                    <span className="text-slate-500">Email:</span>
                                    <a href={`mailto:${selectedContact.email}`} className="text-blue-400 hover:underline">
                                        {selectedContact.email}
                                    </a>
                                </div>
                                {selectedContact.telephone && (
                                    <div className="flex items-center gap-4 text-sm">
                                        <span className="text-slate-500">{t('Téléphone', 'Phone')}:</span>
                                        <span className="text-slate-300">{selectedContact.telephone}</span>
                                    </div>
                                )}
                                {selectedContact.siret && (
                                    <div className="flex items-center gap-4 text-sm">
                                        <span className="text-slate-500">SIRET:</span>
                                        <span className="text-slate-300">{selectedContact.siret}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-4 text-sm">
                                    <span className="text-slate-500">{t('Sujet', 'Subject')}:</span>
                                    <span className="text-slate-300">{selectedContact.sujet}</span>
                                </div>
                                <div className="flex items-center gap-4 text-sm">
                                    <span className="text-slate-500">Date:</span>
                                    <span className="text-slate-300">{formatDate(selectedContact.created_at)}</span>
                                </div>
                            </div>
                            
                            <div className="border-t border-white/10 pt-6">
                                <h4 className="text-sm font-medium text-slate-400 mb-3">Message</h4>
                                <p className="text-slate-300 whitespace-pre-wrap">{selectedContact.message}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="glass-card p-12 text-center">
                            <Mail className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">{t('Sélectionnez un message', 'Select a message')}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Main Admin Component
export const Admin = () => {
    const { isAuthenticated, loading, login } = useAuth();
    const { t } = useLanguage();
    const { theme, toggleTheme } = useTheme();
    const [activeSection, setActiveSection] = useState('dashboard');
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Seed data on first load
    useEffect(() => {
        const seedData = async () => {
            try {
                await axios.post(`${API}/seed`);
            } catch (error) {
                // Ignore if already seeded
            }
        };
        seedData();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <AdminLogin onLogin={login} />;
    }

    const formationsFields = [
        { name: 'titre', label: 'Titre (FR)', type: 'text' },
        { name: 'titre_en', label: 'Titre (EN)', type: 'text' },
        { name: 'organisme', label: 'Organisme', type: 'text' },
        { name: 'annee', label: t('Année', 'Year'), type: 'text' },
        { name: 'description', label: 'Description (FR)', type: 'textarea' },
        { name: 'description_en', label: 'Description (EN)', type: 'textarea' },
        { name: 'type', label: 'Type', type: 'select', options: [
            { value: 'diplome', label: t('Diplôme', 'Degree') },
            { value: 'certification', label: 'Certification' },
            { value: 'formation', label: 'Formation' }
        ]},
        { name: 'document_url', label: 'URL Document', type: 'text' },
    ];

    const experiencesFields = [
        { name: 'client', label: 'Client', type: 'text' },
        { name: 'role', label: t('Rôle (FR)', 'Role (FR)'), type: 'text' },
        { name: 'role_en', label: t('Rôle (EN)', 'Role (EN)'), type: 'text' },
        { name: 'date_debut', label: t('Date début', 'Start date'), type: 'text' },
        { name: 'date_fin', label: t('Date fin', 'End date'), type: 'text' },
        { name: 'localisation', label: 'Localisation', type: 'text' },
        { name: 'type_mission', label: 'Type mission', type: 'select', options: [
            { value: 'principale', label: t('Principale', 'Main') },
            { value: 'parallele', label: t('Parallèle', 'Parallel') }
        ]},
        { name: 'description', label: 'Description (FR)', type: 'textarea' },
        { name: 'description_en', label: 'Description (EN)', type: 'textarea' },
        { name: 'realisations', label: t('R\u00e9alisations cl\u00e9s (FR)', 'Key achievements (FR)'), type: 'array' },
        { name: 'realisations_en', label: t('R\u00e9alisations cl\u00e9s (EN)', 'Key achievements (EN)'), type: 'array' },
        { name: 'technos', label: 'Technologies', type: 'array' },
        { name: 'contexte', label: 'Contexte (FR)', type: 'text' },
        { name: 'contexte_en', label: 'Contexte (EN)', type: 'text' },
    ];

    const servicesFields = [
        { name: 'nom', label: 'Nom (FR)', type: 'text' },
        { name: 'nom_en', label: 'Nom (EN)', type: 'text' },
        { name: 'description', label: 'Description (FR)', type: 'textarea' },
        { name: 'description_en', label: 'Description (EN)', type: 'textarea' },
        { name: 'categorie', label: t('Catégorie', 'Category'), type: 'select', options: [
            { value: 'Développement', label: t('Développement', 'Development') },
            { value: 'Conseil', label: t('Conseil', 'Consulting') },
            { value: 'Formation', label: 'Formation' }
        ]},
        { name: 'icon', label: t('Icône', 'Icon'), type: 'select', options: [
            { value: 'Globe', label: 'Globe' },
            { value: 'Smartphone', label: 'Smartphone' },
            { value: 'Monitor', label: 'Monitor' },
            { value: 'FileText', label: 'FileText' },
            { value: 'Search', label: 'Search' },
            { value: 'Rocket', label: 'Rocket' },
            { value: 'Layout', label: 'Layout' },
            { value: 'Network', label: 'Network' },
            { value: 'GraduationCap', label: 'GraduationCap' }
        ]},
    ];

    const renderSection = () => {
        switch (activeSection) {
            case 'dashboard':
                return <DashboardSection />;
            case 'presentation':
                return <PresentationSection />;
            case 'technologies':
                return <TechnologiesSection />;
            case 'formations':
                return (
                    <CrudSection 
                        title="Formations" 
                        endpoint="formations" 
                        fields={formationsFields}
                        renderItem={(item) => (
                            <div>
                                <span className="text-white font-medium">{item.titre}</span>
                                <span className="text-slate-400 ml-2">— {item.organisme} ({item.annee})</span>
                                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                                    item.type === 'diplome' ? 'bg-blue-500/20 text-blue-400' :
                                    item.type === 'certification' ? 'bg-violet-500/20 text-violet-400' :
                                    'bg-cyan-500/20 text-cyan-400'
                                }`}>
                                    {item.type}
                                </span>
                            </div>
                        )}
                    />
                );
            case 'experiences':
                return (
                    <CrudSection 
                        title={t('Expériences', 'Experiences')} 
                        endpoint="experiences" 
                        fields={experiencesFields}
                        renderItem={(item) => (
                            <div>
                                <span className="text-white font-medium">{item.role}</span>
                                <span className="text-blue-400 ml-2">@ {item.client}</span>
                                <span className="text-slate-500 ml-2">({item.date_debut} - {item.date_fin || t('Présent', 'Present')})</span>
                                {item.type_mission === 'parallele' && (
                                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-violet-500/20 text-violet-400">
                                        {t('Parallèle', 'Parallel')}
                                    </span>
                                )}
                            </div>
                        )}
                    />
                );
            case 'applications':
                return <ApplicationsSection />;
            case 'services':
                return (
                    <CrudSection 
                        title="Services" 
                        endpoint="services" 
                        fields={servicesFields}
                        renderItem={(item) => (
                            <div>
                                <span className="text-white font-medium">{item.nom}</span>
                                <span className="text-slate-400 ml-2">— {item.categorie}</span>
                            </div>
                        )}
                    />
                );
            case 'mailbox':
                return <MailboxSection />;
            case 'payments':
                return <PaymentsSection />;
            default:
                return <DashboardSection />;
        }
    };

    return (
        <div className={`min-h-screen ${theme === 'light' ? 'bg-slate-100' : 'bg-background'}`} data-testid="admin-panel">
            <AdminSidebar 
                activeSection={activeSection} 
                setActiveSection={setActiveSection}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />
            
            {/* Main Content */}
            <div className="lg:ml-64">
                {/* Top bar */}
                <header className={`sticky top-0 z-30 backdrop-blur-xl border-b px-6 py-4 ${theme === 'light' ? 'bg-white/95 border-slate-200' : 'bg-slate-900/95 border-white/10'}`}>
                    <div className="flex items-center justify-between">
                        <button 
                            className={`lg:hidden p-2 ${theme === 'light' ? 'text-slate-800' : 'text-white'}`}
                            onClick={() => setSidebarOpen(true)}
                        >
                            <Menu size={24} />
                        </button>
                        <div className={`text-sm font-medium ${theme === 'light' ? 'text-slate-600' : 'text-slate-400'}`}>
                            DKR CONSULTING — Administration
                        </div>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={toggleTheme}
                                className={`p-2 rounded-full transition-colors ${theme === 'light' ? 'bg-slate-200 text-slate-700 hover:bg-slate-300' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                                data-testid="admin-theme-toggle"
                            >
                                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                            </button>
                            <Link to="/" className={`text-sm flex items-center gap-1 ${theme === 'light' ? 'text-slate-600 hover:text-blue-600' : 'text-slate-400 hover:text-white'}`}>
                                <Eye size={16} />
                                {t('Voir le site', 'View site')}
                            </Link>
                        </div>
                    </div>
                </header>
                
                {/* Content */}
                <main className="p-6 lg:p-12">
                    {renderSection()}
                </main>
            </div>
        </div>
    );
};
